// @ts-nocheck
// Right-hand Sheet with the full LiveCall row + a status timeline. The call
// object is re-read from the live page on every frame so the sheet keeps
// ticking while open; it closes itself when the call hangs up.
import React, { useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dot } from '@/features/cti2/team/teamUi';
import type { LiveCall } from '../types';
import { fmtSpan, sinceMs, statusMeta } from '../liveCallsModel';
import { DirectionCell, RouteCell, StatusPill } from './LiveCallBits';

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-x-3 gap-y-1 text-[12.5px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

interface Props {
  call: LiveCall | null;
  now: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendered under the header (e.g. SnoopButtons for team leads). */
  actions?: React.ReactNode;
}

export default function LiveCallDetailSheet({ call, now, open, onOpenChange, actions }: Props) {
  useEffect(() => {
    if (open && !call) onOpenChange(false);
  }, [open, call, onOpenChange]);

  if (!call) return null;
  const meta = statusMeta(call.status);
  const steps = [{ label: 'Call started', at: call.startedAt }];
  if (call.connectedAt) steps.push({ label: 'Connected', at: call.connectedAt });
  if (call.statusSince && call.statusSince !== call.startedAt && call.statusSince !== call.connectedAt) {
    steps.push({ label: meta.label, at: call.statusSince });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <DirectionCell direction={call.direction} />
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            <StatusPill call={call} now={now} />
            <span className="tabular-nums">total {fmtSpan(sinceMs(now, call.startedAt))}</span>
          </SheetDescription>
        </SheetHeader>

        {actions ? <div className="mt-3">{actions}</div> : null}

        <Separator className="my-4" />

        <dl className="space-y-2">
          <Row label="Customer">
            {call.customerName ? (
              <span className="flex flex-col">
                <span className="font-medium">{call.customerName}</span>
                <span className="tabular-nums text-muted-foreground">{call.customerNo || '—'}</span>
              </span>
            ) : (
              <span className="tabular-nums font-medium">{call.customerNo || '—'}</span>
            )}
          </Row>
          <Row label="DID / CLI"><span className="tabular-nums">{call.did || '—'}</span></Row>
          <Row label="Keys pressed"><span className="tabular-nums">{call.dtmf || '—'}</span></Row>
          <Row label="Source">{call.source || '—'}</Row>
          <Row label="Agent">
            {call.agent ? (
              <span className="flex flex-col">
                <span className="font-medium">{call.agent.name || `Agent ${call.agent.id}`}</span>
                <span className="text-[11px] text-muted-foreground">ext {call.agent.ext || '—'} · channel {call.agent.callId || '—'}</span>
              </span>
            ) : call.aiAgentId ? `AI agent #${call.aiAgentId}` : '—'}
          </Row>
          {call.calleeAgent ? (
            <Row label="Called agent">
              {call.calleeAgent.name || `Agent ${call.calleeAgent.id}`} · ext {call.calleeAgent.ext || '—'}
            </Row>
          ) : null}
          <Row label="Route"><RouteCell call={call} /></Row>
          {call.routingType ? <Row label="Routing">{call.routingType}</Row> : null}
          <Row label="PBX">{call.pbx}</Row>
          <Row label="Trace"><span className="font-mono text-[11px]">{call.traceId}</span></Row>
          {call.requestId ? <Row label="Request"><span className="font-mono text-[11px]">{call.requestId}</span></Row> : null}
          <Row label="Listening">{call.snoopers > 0 ? <Badge variant="secondary">{call.snoopers} supervisor(s)</Badge> : '—'}</Row>
          {call.callGroupIds?.length ? <Row label="Call groups">{call.callGroupIds.join(', ')}</Row> : null}
        </dl>

        <Separator className="my-4" />

        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Timeline</h3>
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={`${s.label}-${s.at}`} className="grid grid-cols-[72px_1fr] items-center gap-3 text-[12px]">
              <span className="tabular-nums text-muted-foreground">{fmtSpan(sinceMs(now, s.at))} ago</span>
              <span className="inline-flex items-center gap-2">
                <Dot tone={i === steps.length - 1 ? 'good' : 'off'} />
                {s.label}
              </span>
            </li>
          ))}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
